import { z } from 'zod';
import { MAX_FILES_PER_MESSAGE, UploadTarget } from './files.js';
import { Priority, StatusCategory } from './work-items.js';
/**
 * Client helpdesk (docs/adr/0027-client-helpdesk.md): guided setup with the workspace's own sending
 * address, support plans with hours, and the client portal.
 */
// --- Emails to clients -------------------------------------------------------------------------------
export const SupportEmailKind = z.enum(['received', 'reply', 'waiting', 'resolved', 'hours', 'statement']);
export const SUPPORT_EMAIL_KINDS = {
    received: { label: 'Ticket received', description: 'The ticket number, and when to expect a reply.' },
    reply: { label: 'New reply', description: "Your team's reply, with a link to the whole conversation." },
    waiting: { label: 'Waiting on them', description: 'When a ticket moves to Waiting on client.' },
    resolved: { label: 'Resolved', description: 'With a one-click rating and how to reopen.' },
    hours: { label: 'Hours alerts', description: 'At 80% and 100% of a support plan.' },
    statement: { label: 'Statements', description: 'Hours used, at the end of each plan period.' },
};
export const SupportClientEmails = z.record(SupportEmailKind, z.boolean());
/**
 * A workspace's own wording for client emails. Plain text: rendered escaped, never as markup. Empty
 * means Chapsi's wording. Placeholders are filled in when the email is sent.
 */
export const EMAIL_PLACEHOLDERS = ['number', 'title', 'workspace'];
const placeholderOk = (s) => [...s.matchAll(/\{([^{}]*)\}/g)].every((m) => EMAIL_PLACEHOLDERS.includes(m[1]));
const Wording = (max) => z
    .string()
    .trim()
    .max(max)
    .refine(placeholderOk, { message: `Use only ${EMAIL_PLACEHOLDERS.map((p) => `{${p}}`).join(', ')}` });
export const WordedEmailKind = z.enum(['received', 'waiting', 'resolved']);
const WordedEmail = z.object({ heading: Wording(120).default(''), body: Wording(1000).default('') });
export const SupportEmailWording = z.object({
    received: WordedEmail.default({ heading: '', body: '' }),
    waiting: WordedEmail.default({ heading: '', body: '' }),
    resolved: WordedEmail.default({ heading: '', body: '' }),
    /** Added at the end of every client email, e.g. office hours or a phone number. */
    signOff: Wording(500).default(''),
});
export const EMPTY_EMAIL_WORDING = {
    received: { heading: '', body: '' },
    waiting: { heading: '', body: '' },
    resolved: { heading: '', body: '' },
    signOff: '',
};
export const SupportEmailPreviewInput = z.object({
    kind: z.enum(['received', 'reply', 'waiting', 'resolved']),
    wording: SupportEmailWording,
    brandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});
export const SupportEmailPreview = z.object({ subject: z.string(), html: z.string(), text: z.string() });
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #6D28D9');
/** The marker client replies are cut at (before quoted history). */
export const REPLY_MARKER = '##- Reply above this line -##';
// --- Setup: address, forwarding, sending domain, test ---------------------------------------------
export const DnsRecord = z.object({
    type: z.enum(['CNAME', 'MX', 'TXT']),
    /** Relative to the domain ("k7f2._domainkey") and in full ("k7f2._domainkey.pixelscope.com.au"). */
    name: z.string(),
    fqdn: z.string(),
    value: z.string(),
    purpose: z.enum(['dkim', 'mail_from_mx', 'mail_from_spf', 'dmarc']),
    status: z.enum(['verified', 'pending', 'failed', 'recommended']),
});
export const SupportSetup = z.object({
    /** The address clients email, e.g. support@pixelscope.com.au. Null until chosen. */
    address: z.string().nullable(),
    /** Where to forward it: <workspace>@<inbound domain>. Null when this server cannot receive mail. */
    forwardTo: z.string().nullable(),
    forwarding: z.object({
        state: z.enum(['waiting', 'confirm', 'working']),
        /** Gmail's forwarding confirmation code, when its confirmation email arrived. */
        confirmationCode: z.string().nullable(),
        confirmationLink: z.string().nullable(),
        lastInboundAt: z.string().nullable(),
    }),
    sending: z.object({
        domain: z.string().nullable(),
        state: z.enum(['not_started', 'pending', 'verified', 'failed', 'free_mail', 'taken', 'unavailable']),
        records: z.array(DnsRecord),
        lastCheckedAt: z.string().nullable(),
        /** What replies go out from right now. */
        fromAddress: z.string(),
    }),
    test: z.object({ sentAt: z.string().nullable(), receivedAt: z.string().nullable(), seconds: z.number().nullable() }),
    /** The portal's address, when the Client portal feature is on. */
    portalUrl: z.string().nullable(),
    brandColor: HexColor,
    clientEmails: SupportClientEmails,
    wording: SupportEmailWording,
});
export const UpdateSupportSetupInput = z.object({
    address: z.email().trim().toLowerCase().max(320).nullable().optional(),
    brandColor: HexColor.optional(),
    clientEmails: SupportClientEmails.optional(),
    wording: SupportEmailWording.optional(),
});
export const ShareDnsRecordsInput = z.object({ email: z.email().trim().toLowerCase().max(320) });
// --- Support plans ------------------------------------------------------------------------------------
export const PlanPeriodKind = z.enum(['month', 'quarter', 'year']);
export const PlanOverage = z.enum(['continue', 'ask', 'pause']);
export const SupportPlan = z.object({
    clientId: z.uuid(),
    /** Hours per period; null means no plan (the portal settings still apply). */
    hours: z.number().positive().max(10000).nullable(),
    period: PlanPeriodKind,
    startsOn: z.iso.date(),
    carryOver: z.enum(['none', 'capped', 'unlimited']),
    carryCapPercent: z.number().int().min(1).max(100),
    overage: PlanOverage,
    countTasks: z.boolean(),
    roundingMinutes: z.union([z.literal(0), z.literal(6), z.literal(15)]),
    alertClient: z.boolean(),
    alertTeam: z.boolean(),
    sendStatement: z.boolean(),
    billingEmails: z.array(z.email().toLowerCase()).max(10),
    showHours: z.boolean(),
    showTicketTime: z.boolean(),
    showTasks: z.boolean(),
    companyTickets: z.boolean(),
});
export const UpdateSupportPlanInput = SupportPlan.omit({ clientId: true }).partial();
export const PlanUsage = z.object({
    periodStart: z.string(),
    /** Exclusive. The plan renews on this date. */
    periodEnd: z.string(),
    allowanceMinutes: z.number().int(),
    carriedMinutes: z.number().int(),
    usedMinutes: z.number().int(),
    leftMinutes: z.number().int(),
    overMinutes: z.number().int(),
    percent: z.number().int(),
});
export const SupportPlanResponse = z.object({
    plan: SupportPlan,
    /** Null without hours, or before the plan starts. */
    usage: PlanUsage.nullable(),
    /** False when no plan has been saved yet (the response shows the defaults). */
    saved: z.boolean(),
});
// --- Client portal ------------------------------------------------------------------------------------
export const PortalStatus = z.object({ label: z.string(), category: StatusCategory, color: z.string() });
export const PortalSession = z.object({
    workspace: z.object({ name: z.string(), slug: z.string(), brandColor: HexColor }),
    contact: z.object({ name: z.string().nullable(), email: z.string(), emailLess: z.boolean() }),
    clientName: z.string().nullable(),
    /** 'ticket': opened from an email link, sees that one ticket. 'all': signed in with a sign-in link. */
    scope: z.enum(['ticket', 'all']),
    ticketId: z.uuid().nullable(),
    show: z.object({
        hours: z.boolean(),
        ticketTime: z.boolean(),
        tasks: z.boolean(),
        /** Monthly hours reports for projects the team shared (docs/adr/0042); needs Time on. */
        reports: z.boolean().default(false),
    }),
    /** The largest file a contact may attach, in MB. */
    maxUploadMb: z.number().int(),
    /**
     * The workspace is read-only for billing (docs/adr/0044): the portal shows its write controls disabled with
     * a short explanation instead of letting them fail. Optional for older servers.
     */
    readOnly: z.boolean().optional(),
});
export const PortalTicketSummary = z.object({
    id: z.uuid(),
    number: z.number().int(),
    title: z.string(),
    status: PortalStatus,
    raisedBy: z.string(),
    lastUpdate: z.object({ text: z.string(), at: z.string() }),
    /** Time logged, when the client may see it. */
    minutes: z.number().int().nullable(),
    createdAt: z.string(),
});
export const PortalTimelineEntry = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('message'),
        id: z.uuid(),
        /** 'client': written by the client (email or portal); 'team': sent by the agency. */
        from: z.enum(['client', 'team']),
        authorName: z.string(),
        via: z.enum(['email', 'portal']),
        body: z.string(),
        /** `url` is a signed download link, valid for an hour; null when the file wasn't kept (too large or blocked). */
        attachments: z.array(z.object({ name: z.string(), size: z.number().int(), contentType: z.string(), url: z.string().nullable() })),
        at: z.string(),
    }),
    z.object({ kind: z.literal('status'), label: z.string(), category: StatusCategory, at: z.string() }),
]);
export const PortalTicketDetail = PortalTicketSummary.extend({
    priority: Priority,
    assigneeName: z.string().nullable(),
    firstReplyMinutes: z.number().int().nullable(),
    timeline: z.array(PortalTimelineEntry),
    watchers: z.array(z.object({ name: z.string(), you: z.boolean() })),
    satisfaction: z.enum(['great', 'ok', 'bad']).nullable(),
    /** The client may add colleagues on these domains. */
    colleagueDomains: z.array(z.string()),
    /** A resolved ticket can be reopened by a reply until then; null when open or past the window. */
    reopenUntil: z.string().nullable(),
    /** With "ask first" or "pause": the plan's hours are used up. */
    planNotice: z.enum(['ask', 'pause']).nullable(),
    /** The ticket asked for was merged into this one (docs/adr/0102): its number, for "#1001 continues here". */
    mergedFromNumber: z.number().int().nullable().optional(),
});
export const PortalTicketList = z.object({ tickets: z.array(PortalTicketSummary) });
export const PortalOverview = z.object({
    plan: z
        .object({ hours: z.number(), period: PlanPeriodKind, usage: PlanUsage })
        .nullable(),
    tickets: z.object({ open: z.number().int(), waitingOnYou: z.number().int(), resolvedThisYear: z.number().int() }),
    /** Average first reply in the last 30 days, in minutes; null without replies. */
    firstReplyMinutes: z.number().int().nullable(),
    openTickets: z.array(PortalTicketSummary),
    tasks: z
        .array(z.object({ id: z.uuid(), title: z.string(), projectName: z.string(), status: PortalStatus, completedAt: z.string().nullable() }))
        .nullable(),
    hoursByMonth: z.array(z.object({ month: z.string(), minutes: z.number().int() })).nullable(),
    /**
     * Progress updates the team sent to this contact (docs/adr/0063), newest first: only ones addressed to them, and
     * none in a portal opened from a single ticket's link.
     */
    updates: z.array(z.object({ id: z.uuid(), projectName: z.string(), subject: z.string(), body: z.string(), sentAt: z.string() })),
});
export const PlanStatement = z.object({
    clientName: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    usage: PlanUsage,
    entries: z.array(z.object({ date: z.string(), ticket: z.string(), minutes: z.number().int() })),
});
export const PortalSignInLinkInput = z.object({
    workspace: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
    email: z.email().trim().toLowerCase().max(320),
});
export const PortalExchangeInput = z.object({ token: z.string().min(20).max(100) });
const FileIds = z.array(z.uuid()).max(MAX_FILES_PER_MESSAGE).default([]);
export const PortalReplyInput = z
    .object({ body: z.string().trim().max(20000).default(''), fileIds: FileIds })
    .refine((v) => v.body.length > 0 || v.fileIds.length > 0, { message: 'Write a reply or attach a file', path: ['body'] });
export const PortalNewTicketInput = z.object({
    subject: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(20000),
    priority: z.enum(['normal', 'urgent']).default('normal'),
    fileIds: FileIds,
});
/** A file a contact is about to attach. The bytes go straight to storage; a reply or new request then claims it. */
export const PortalUploadInput = z.object({
    name: z.string().trim().min(1).max(255),
    size: z.number().int().min(1),
    contentType: z.string().trim().min(1).max(255).regex(/^[\w.+-]+\/[\w.+-]+$/, 'Unknown file type'),
});
export const PortalCreatedUpload = z.object({ fileId: z.uuid(), upload: UploadTarget });
export const PortalRatingInput = z.object({ rating: z.enum(['great', 'ok', 'bad']), comment: z.string().trim().max(2000).optional() });
export const PortalWatcherInput = z.object({ email: z.email().trim().toLowerCase().max(320), name: z.string().trim().max(200).optional() });
export const PortalPreferencesInput = z.object({ emailLess: z.boolean() });
export const PortalStatementQuery = z.object({ period: z.iso.date().optional() });
export const PortalCreatedTicket = z.object({ id: z.uuid(), number: z.number().int() });
//# sourceMappingURL=helpdesk.js.map